import axios, { type InternalAxiosRequestConfig } from 'axios';
import { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MENU_CONFIG } from '../data/menu';
import toast from 'react-hot-toast';
import { jwtDecode } from "jwt-decode";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// リトライ判定用の拡張プロパティ定義
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// リフレッシュ処理のPromiseを保持する変数
// ※複数のAPI実行を非同期で同時実行した場合にリフレッシュが複数回実行されないように制御する
let refreshPromise: Promise<string> | null = null;
let isLoggingOut = false;

export const useApiClient = () => {
  // Contextからトークンと更新用関数を取得
  const { accessToken, logout, updateAccessToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const apiClient = useMemo(() => {
    // 1. axiosインスタンス生成
    const instance = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // 2. リクエストインターセプター
    // アクセストークンがあればヘッダーに付与する
    instance.interceptors.request.use(
      async (config) => {
        // 型キャスト
        const customConfig = config as CustomAxiosRequestConfig;

        // すでにログアウト処理中なら、一切の通信を遮断する
        if (isLoggingOut) {
          return Promise.reject(new axios.Cancel("Session expired. Request cancelled."));
        }

        let pageName = "不明な画面";
        // 全カテゴリーの中から path が一致する item を探す
        for (const cat of MENU_CONFIG) {
          const foundItem = cat.items.find(item => item.path === location.pathname);
          if (foundItem) {
            pageName = foundItem.name;
            break;
          }
        }
        // 日本語を安全に送るためにエンコード
        config.headers['X-Update-Page'] = encodeURIComponent(pageName);

        // リトライ実行時（_retry = true）は、ここでヘッダーを触らない
        // (レスポンスインターセプター側で既に「最新のトークン」をセットしているため)
        if (customConfig._retry) {
          return config;
        }

        // Context（フロントエンドのメモリ）にアクセストークンが存在している場合のみ、以下のトークン検証および付与処理を行う
        if (accessToken) {

          let isExpired = false;  // 有効期限切れフラグ：アクセストークン有効期限切れの場合:true
          
          // 1. アクセストークンの有効期限をチェック
          try {
            const decoded = jwtDecode(accessToken);
            const currentTime = Date.now() / 1000;
            // アクセストークンの有効期限が期限切れ、もしくは期限切れの10秒以内に迫っている場合＝＞有効期限切れフラグON
            if (decoded.exp && decoded.exp < currentTime + 10) {
              isExpired = true;
            }
          } catch (e) {
            // デコード失敗時は期限切れ扱いにはせず、そのまま送る（バックエンドに弾かせる）
          }

          // 2. 期限切れの場合のリフレッシュ処理
          if (isExpired) {

            // 並列での処理実行を指示されているAPI実行指示のうち1つ目のAPI実行指示についてのみ下記処理を行う
            if (!refreshPromise) {
              console.log("Token is about to expire. Refreshing BEFORE sending request...");
              refreshPromise = (async () => {
                try {
                  // リフレッシュ実行
                  const refreshResponse = await axios.post(
                    `${API_BASE_URL}/auth/refresh`,
                    {},
                    { withCredentials: true }
                  );
                  // リフレッシュ実行結果より新規のアクセストークンを取得
                  const newAccessToken = refreshResponse.data.newAccessToken;
                  updateAccessToken(newAccessToken);
                  return newAccessToken as string;
                } catch (e:any) {
                  // リフレッシュエラーの場合(リフレッシュトークン有効期限切れ、同時接続数エラーなど)、例外をthrow
                  refreshPromise = null;

                  // 即座にログアウトフラグを立てて、後続のAPIを入り口で遮断させる
                  if (!isLoggingOut) {
                    isLoggingOut = true;
                    
                    const backendMessage = e.response?.data?.message;
                    if (backendMessage) {
                      toast.error(backendMessage, { id: 'session-expire', duration: 5000 });
                    } else {
                      toast.error("セッションの有効期限が切れました。再度ログインしてください。", { id: 'session-expire', duration: 4000 });
                    }
                    
                    logout().finally(() => {
                      navigate('/login');
                      setTimeout(() => { isLoggingOut = false; }, 2000);
                    });
                  }

                  throw e; // エラーを外に投げる
                } finally {
                  setTimeout(() => { refreshPromise = null; }, 3000);
                }
              })();
            }

            try {
              // リフレッシュ実行　※複数並列処理の場合、1つ目のAPIに対してのみリフレッシュが1回実行され、その他のAPIを含め全てここで、リフレッシュの完了を待機する
              const newToken = await refreshPromise;
              if (newToken) {
                // リフレッシュが成功したら再取得したアクセストークンをAuthorizationにセットして、待機中のAPI実行を継続する
                config.headers.Authorization = `Bearer ${newToken}`;
              }
            } catch (refreshError) {
              // ==========================================================
              // リフレッシュが失敗（401等）した場合は、元のAPI通信を「強行」せず、
              // ここでリクエストを破棄（Reject）してバックエンドに飛ばさない！！
              // ==========================================================
              return Promise.reject(refreshError);
            }
          } else {
            // まだ有効期限内ならそのままセット
            config.headers.Authorization = `Bearer ${accessToken}`;
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // 3. レスポンスインターセプター
    // 401エラー（期限切れ）を検知して、自動リフレッシュを行う
    instance.interceptors.response.use(
      (response) => response,
      async (error: any) => {

        // Requestインターセプターで「意図的にキャンセル(Reject)」したものは無視する
        if (axios.isCancel(error)) {
          return Promise.reject(error);
        }

        const originalRequest = error.config as CustomAxiosRequestConfig;
        
        // そもそも失敗したのが「リフレッシュAPI自身」なら、絶対に再リトライしない（無限ループ防止）
        if (originalRequest?.url?.includes('/auth/refresh')) {
          return Promise.reject(error);
        }

        // すでにログアウト処理が始まっているなら、もう何もしない
        if (isLoggingOut) {
          return Promise.reject(error);
        }

        if (error.response?.status === 403) {
          toast.error("この操作を行う権限がありません。", { id: 'forbidden-error', duration: 4000 });
          // 必要に応じて navigate('/') などでトップへ戻す処理を入れてもOKです
          return Promise.reject(error);
        }

        // 401エラー かつ、まだリトライしていない場合
        if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
          
          originalRequest._retry = true; // 無限ループ防止フラグ

          try {
            // リフレッシュ処理の共有ロジック
            // まだ誰もリフレッシュを開始していない場合のみ、リクエストを発行する
            if (!refreshPromise) {
              console.log("AccessToken expired. Starting refresh process...");
              
              refreshPromise = (async () => {
                try {
                  const refreshResponse = await axios.post(
                    `${API_BASE_URL}/auth/refresh`,
                    {},
                    { withCredentials: true }
                  );
                  const newAccessToken = refreshResponse.data.newAccessToken; // または accessToken
                  
                  // Context更新
                  updateAccessToken(newAccessToken);
                  return newAccessToken;
                } catch (e) {
                  // 失敗したらPromiseも破棄してエラーを投げる
                  refreshPromise = null;
                  throw e;
                } finally {
                  // 成功しても失敗しても、処理が終わったら変数をクリアする
                  // (ただし、後続のawaitが完了するまで少し遅延させた方が安全な場合もある)
                  refreshPromise = null;
                }
              })();
            }

            // 全員ここで待機する
            // 最初に実行した人も、後から来た人も、同じ Promise の結果を待つ
            const token = await refreshPromise;

            if (token) {
              // 新しいトークンでヘッダーを書き換えて再実行
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            }

          } catch (refreshError: any) {
            console.error('Session expired. Logging out.', refreshError);
            refreshPromise = null; // 念の為クリア

            // const backendMessage = refreshError.response?.data?.message;
            
            // if (backendMessage) {
            //   // 「最大接続数に達するログインがあったため...」などがここで表示される
            //   toast.error(backendMessage, { duration: 5000 });
            // } else {
            //   // バックエンドからメッセージが無い場合の汎用メッセージ
            //   toast.error("セッションの有効期限が切れました。再度ログインしてください。", { duration: 4000 });
            // }
            
            // await logout();
            // navigate('/login');

            if (!isLoggingOut) {
              isLoggingOut = true;

              const backendMessage = refreshError.response?.data?.message;
              
              if (backendMessage) {
                // id を指定することで、万が一複数呼ばれてもトーストが1つしか出ないようにする（react-hot-toastの機能）
                toast.error(backendMessage, { id: 'session-expire', duration: 5000 });
              } else {
                toast.error("セッションの有効期限が切れました。再度ログインしてください。", { id: 'session-expire', duration: 4000 });
              }
              
              // 非同期処理でログアウトと画面遷移を行う
              logout().finally(() => {
                navigate('/login');
                // 画面遷移が終わった頃合い（例: 2秒後）にフラグを解除し、次回のログイン以降に備える
                setTimeout(() => { isLoggingOut = false; }, 2000);
              });
            }

            return Promise.reject(refreshError);
          }
        }

        // 401以外、またはリトライ済みのエラーはそのまま返す
        return Promise.reject(error);
      }
    );

    return instance;
  }, [accessToken, logout, navigate, updateAccessToken]);

  return apiClient;
};